/* eslint-disable prettier/prettier */
import { useState, useEffect, useContext, useCallback, Suspense } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Tab, Tabs, Box } from "@material-ui/core";
import UserManagementGrid from "./userManagementGrid/UserManagementGrid";
import { ADMIN_DASHBOARD_HEADER } from "../../../../constants/caption/dashboardHeader";
import { AuthContext, UIContext } from "../../../../contexts";
import { api } from "../../../../utils/api";
import TabPanel from "../../../shared/tabPanelComponent/TabPanel.jsx";
import a11yProps from "../../../../constants/tabPanelProps/ayProps";
import useRouteParam from "../../../../hooks/useRouteParam";
import ROLES from "../../../../constants/roles";
import PersonAddIcon from "@material-ui/icons/PersonAdd";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-alpine.css";
import "./userManagement.css";
import { useEmployerRoute, useEnroller } from "../../../../hooks";
import { trackPromise } from "react-promise-tracker";
import { CustomPagination } from "../../../shared";
import { PaginationTypes } from "../../../../@types/pagination.types";
import { is } from "date-fns/locale";
import { getPermissionsFromGroupOwner } from "../../../../utils/getPermissionsFromGroupOwner";

const UserManagement = () => {
	const { isEnroller } = useEnroller();
	const [enabledPagination, setEnabledPagination] = useState<PaginationTypes>({
		currentPage: 1,
		pageCount: undefined
	});
	const [disabledPagination, setDisabledPagination] = useState<PaginationTypes>({
		currentPage: 1,
		pageCount: undefined
	});
	const { currentPage: enabledPage, pageCount: enabledPageCount } = enabledPagination;
	const { currentPage: disabledPage, pageCount: disabledPageCount } = disabledPagination;
	const [enabledGroupOwners, setEnabledGroupOwners] = useState<any[]>([]);
	const [disabledGroupOwners, setDisabledGroupOwners] = useState<any[]>([]);
	const [value, setValue] = useState<number>(0);
	const navigate = useNavigate();
	const isEmployerRoute = useEmployerRoute();
	const { user } = useContext(AuthContext);
	const queryGroupNumber = useRouteParam("gn");
	const { setDashboardHeader, setSelectedTab } = useContext(UIContext);

	const handleEnabledPageChange = useCallback((event: React.ChangeEvent<unknown>, page: number) => {
		//eslint-disable-next-line
		setEnabledPagination((prev) => ({ ...prev, currentPage: page }));
	}, []);

	const handleDisabledPageChange = useCallback((event: React.ChangeEvent<unknown>, page: number) => {
		//eslint-disable-next-line
		setDisabledPagination((prev) => ({ ...prev, currentPage: page }));
	}, []);

	const [loading, setLoading] = useState(false);
	const [filterModel, setFilterModel] = useState<any>({});

	const [tabs, setTabs] = useState<any[]>([]);

	const [userApprovalFilterSchema, setUserApprovalFilterSchema] = useState({
		user_id: { value: null, type: null },
		alternate_id: { value: null, type: null },
		first_name: { value: null, type: null },
		middle_name: { value: null, type: null },
		last_name: { value: null, type: null },
		user_name: { value: null, type: null },
		password: { value: null, type: null },
		role: { value: null, type: null },
		email: { value: null, type: null },
		SSN: { value: null, type: null },
		date_of_birth: { value: null, type: null },
		gender: { value: null, type: null },
		marital_status: { value: null, type: null },
		city: { value: null, type: null },
		state: { value: null, type: null },
		country: { value: null, type: null },
		ZIP: { value: null, type: null },
		address_line_1: { value: null, type: null },
		address_line_2: { value: null, type: null },
		contact_label: { value: null, type: null },
		mobile: { value: null, type: null },
		phone_extension: { value: null, type: null },
		is_verified: { value: null, type: null },
		is_locked_out: { value: null, type: null },
		is_active: { value: null, type: null },
		is_inactive: { value: null, type: null },
		is_online: { value: null, type: null },
		approved_by: { value: null, type: null },
		created_by: { value: null, type: null },
		is_approved: { value: null, type: null },
		is_registered: { value: null, type: null },
		assigned_groups: { value: null, type: null },
		security_question: { value: null, type: null },
		security_answer: { value: null, type: null },
		date: { value: null, type: null },
		last_login_date: { value: null, type: null },
		is_member_support: { value: null, type: null },
		is_employer_support: { value: null, type: null },
		hire_date: { value: null, type: null },
		upload_type: { value: null, type: null },
		claim_notification: { value: null, type: null }
	});

	const userManagementPermission =
		user && isEmployerRoute && queryGroupNumber
			? getPermissionsFromGroupOwner(user, Number(queryGroupNumber))?.employer_settings_menu.user_management_sub
					.self
			: user
			? getPermissionsFromGroupOwner(user, 0)?.settings_menu.user_management_sub.self
			: null;

	const handleLabelChange = (event: React.ChangeEvent<unknown>, value: number) => {
		setValue(value);
	};

	const handleAddUserClick = useCallback(() => {
		const pathname = isEmployerRoute === true ? `/employer/create-user?gn=${queryGroupNumber}` : "/create-user";
		navigate(pathname);
	}, [isEmployerRoute, navigate, queryGroupNumber]);

	const getGroupOwners = useCallback(
		async (filters = {}) => {
			try {
				setLoading(true);
				if (isEmployerRoute) {
					if (value === 0) {
						const [enabledRes] = await Promise.all([
							trackPromise(
								api.user.getEmployers({
									group_number: Number(queryGroupNumber),
									role: ROLES.employer,
									page: enabledPage,
									limit: 10,
									filtering: filters,
									is_disabled: false
								})
							)
						]);
						if (enabledRes?.result && enabledRes?.pagination) {
							setEnabledGroupOwners(enabledRes.result);
							setEnabledPagination({
								currentPage: enabledRes.pagination.page,
								pageCount: enabledRes.pagination.totalPages
							});
						}
					} else if (value === 1) {
						const [disabledRes] = await Promise.all([
							trackPromise(
								api.user.getEmployers({
									group_number: Number(queryGroupNumber),
									role: ROLES.employer,
									page: disabledPage,
									limit: 10,
									filtering: filters,
									is_disabled: true
								})
							)
						]);
						if (disabledRes?.result && disabledRes?.pagination) {
							setDisabledGroupOwners(disabledRes.result);
							setDisabledPagination({
								currentPage: disabledRes.pagination.page,
								pageCount: disabledRes.pagination.totalPages
							});
						}
					}
				} else {
					const [enabledRes, disabledRes] = await Promise.all([
						trackPromise(
							api.user.getAssignedGroupOwners({
								page: enabledPage,
								limit: 10,
								is_disabled: false,
								filters: filters
							})
						),
						trackPromise(
							api.user.getAssignedGroupOwners({
								page: disabledPage,
								limit: 10,
								is_disabled: true,
								filters: filters
							})
						)
					]);
					if (enabledRes?.result && enabledRes?.pagination) {
						setEnabledGroupOwners(enabledRes.result);
						setEnabledPagination({
							currentPage: enabledRes.pagination.page,
							pageCount: enabledRes.pagination.totalPages
						});
					}
					if (disabledRes?.result && disabledRes?.pagination) {
						setDisabledGroupOwners(disabledRes.result);
						setDisabledPagination({
							currentPage: disabledRes.pagination.page,
							pageCount: disabledRes.pagination.totalPages
						});
					}
				}
			} catch (error) {
				console.error("Error fetching group owners:", error);
			} finally {
				setLoading(false);
			}
		},
		[isEmployerRoute, value, queryGroupNumber, enabledPage, disabledPage]
	);

	const handleFilterChange = useCallback(
		(filterModel: any) => {
			setFilterModel(filterModel);
			getGroupOwners(filterModel);
		},
		[getGroupOwners]
	);

	const handleTabRenders = useCallback(() => {
		const generatedTabs: any[] = [];

		// Enabled Users Tab

		if (userManagementPermission && userManagementPermission.enabled_list_view) {
			generatedTabs.push({
				label: "Enabled User",
				component: (
					<Suspense fallback={<div />}>
						<UserManagementGrid
							gridData={enabledGroupOwners}
							gridFilterSchema={userApprovalFilterSchema}
							setGridFilterSchema={setUserApprovalFilterSchema}
							onFilterChange={handleFilterChange}
							loading={loading}
						/>
					</Suspense>
				)
			});
		}

		// Disabled Users Tab
		if (userManagementPermission && userManagementPermission.disabled_list_view) {
			generatedTabs.push({
				label: "Disabled User",
				component: (
					<Suspense fallback={<div />}>
						<UserManagementGrid
							gridData={disabledGroupOwners}
							gridFilterSchema={userApprovalFilterSchema}
							setGridFilterSchema={setUserApprovalFilterSchema}
							onFilterChange={handleFilterChange}
							loading={loading}
						/>
					</Suspense>
				)
			});
		}

		setTabs(generatedTabs);
	}, [
		enabledGroupOwners,
		disabledGroupOwners,
		userManagementPermission,
		userApprovalFilterSchema,
		setUserApprovalFilterSchema,
		handleFilterChange,
		loading
	]);
	// console.log("isEmployerRoute", isEmployerRoute);

	useEffect(() => {
		setDashboardHeader(ADMIN_DASHBOARD_HEADER.user_management);

		if (isEmployerRoute !== null) {
			getGroupOwners();
		}
	}, [getGroupOwners, setDashboardHeader, isEmployerRoute, enabledPage, disabledPage]);

	useEffect(() => {
		if (enabledGroupOwners.length || disabledGroupOwners.length) {
			handleTabRenders();
		}
	}, [enabledGroupOwners, disabledGroupOwners, handleTabRenders]);

	// console.log("isEmployerRoute 2", filterModel);

	return (
		<div id="user-approval" className="user-approval">
			{userManagementPermission && userManagementPermission.create ? (
				<div className="pf-action-button-container" id="pf-action-button-container">
					<Button
						className="button-green"
						onClick={handleAddUserClick}
						variant="contained"
						style={{ cursor: "pointer", pointerEvents: "unset" }}
					>
						<span className="button-label-with-icon">Add User</span>
						<span>
							<PersonAddIcon className="button-icon" />
						</span>
					</Button>
				</div>
			) : null}
			<div className="user-management-tab-panel-container ">
				<Box sx={{ width: "100%" }}>
					<Box sx={{ borderBottom: 1, borderColor: "divider" }}>
						<Tabs
							value={value}
							onChange={handleLabelChange}
							aria-label="basic tabs example"
							TabIndicatorProps={{ style: { border: "none", backgroundColor: "#558b2f" } }}
							className="glb-tab-panel"
						>
							{tabs.map((tab, idx) => (
								<Tab key={idx} label={tab.label} className="glb-tab-label" {...a11yProps(idx)} />
							))}
						</Tabs>
					</Box>
					{/* <TabPanel value={value} index={0}>
						<Suspense fallback={<div />}>
							<UserManagementGrid
								gridData={enabledGroupOwners}
								gridFilterSchema={userApprovalFilterSchema}
								setGridFilterSchema={setUserApprovalFilterSchema}
								onFilterChange={handleFilterChange}
								loading={loading}
							/>
						</Suspense>
					</TabPanel>
					<TabPanel value={value} index={1}>
						<Suspense fallback={<div />}>
							<UserManagementGrid
								gridData={disabledGroupOwners}
								gridFilterSchema={userApprovalFilterSchema}
								setGridFilterSchema={setUserApprovalFilterSchema}
								onFilterChange={handleFilterChange}
								loading={loading}
							/>
						</Suspense>
					</TabPanel> */}
					{tabs.map((tab, idx) => (
						<TabPanel key={idx} value={value} index={idx}>
							{tab.component}
						</TabPanel>
					))}
				</Box>
			</div>
			<div className="custom-pagination" id="custom-pagination">
				{value === 0 && (
					<CustomPagination
						pageCount={enabledPageCount}
						currentPage={enabledPage}
						handlePageChange={handleEnabledPageChange}
					/>
				)}
				{value === 1 && (
					<CustomPagination
						pageCount={disabledPageCount}
						currentPage={disabledPage}
						handlePageChange={handleDisabledPageChange}
					/>
				)}
			</div>
		</div>
	);
};

export default UserManagement;
